/**
 * Product Setup Wizard — see snippets/product-wizard.liquid
 *
 * Presents the product's variants as a series of questions. One question is
 * open at a time; answered ones collapse to a summary row with a Change
 * button, and the next one scrolls into view. The final answer on each branch
 * names a variant outright (a metaobject variant reference), so nothing here
 * depends on option names or values — renaming an option in the admin cannot
 * break the flow.
 *
 * The chosen variant id is written straight into the product form's hidden
 * `id` input. Price, SKU, inventory and the Add to cart state are refreshed by
 * reusing VariantSelects' own renderProductInfo() (assets/product.js).
 */
if (!customElements.get('product-wizard')) {
  /** Long enough to read the tick before the step collapses. */
  const COLLAPSE_DELAY = 400;
  /** How long a step takes to open or collapse. */
  const STEP_ANIMATION = 500;

  /** Clears the sticky site header when scrolling a step into view. */
  const SCROLL_MARGIN = 90;

  /**
   * CSS.escape is missing in older Safari, and an exception thrown from a
   * selector would take the whole render with it. The ids here are built from
   * a section id and a metaobject key, so escaping the CSS specials is enough.
   */
  const escapeId = (value) =>
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(value)
      : String(value).replace(/([^\w-])/g, '\\$1');

  class ProductWizard extends HTMLElement {
    connectedCallback() {
      this.productWrapper = this.closest('.thb-product-detail') || document;
      this.variantSelects = this.productWrapper.querySelector('variant-selects');

      this.steps = Array.from(this.querySelectorAll('[data-wizard-step]'));
      this.deduplicateStepIds();
      this.warrantyStep = this.querySelector('[data-wizard-warranty]');
      this.summary = this.querySelector('[data-wizard-summary]');
      this.summaryBody = this.querySelector('[data-wizard-summary-body]');
      this.summaryTotal = this.querySelector('[data-wizard-summary-total]');
      this.remaining = this.querySelector('[data-wizard-remaining]');
      if (!this.steps.length) return;

      // Outside this element, so the fixed bar is not trapped by a transform.
      this.sticky = document.querySelector('[data-wizard-sticky]');

      this.adoptWarrantyPicker();
      this.autoSelectSingleChoices();
      this.setAttribute('data-wizard-active', '');

      // The step the customer is working on. Everything answered above it
      // collapses; everything below it stays hidden.
      this.currentStep = this.steps[0];

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      // Hover (pointer devices only) and keyboard focus open the popover.
      // Delegated, and `focusin`/`focusout` rather than focus/blur, because
      // those do bubble.
      this.addEventListener('mouseover', this.onPointerOver);
      this.addEventListener('mouseout', this.onPointerOut);
      this.addEventListener('focusin', this.onPointerOver);
      this.addEventListener('focusout', this.onPointerOut);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);

      this.watchStickyVisibility();

      // From the start, not only once a variant resolves: the theme can
      // re-render the button at any point in the flow.
      this.watchForRerenders();
      this.render();
    }

    disconnectedCallback() {
      if (this.rerenderObserver) this.rerenderObserver.disconnect();
      if (this.stickyObserver) this.stickyObserver.disconnect();
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      if (this.collapseTimer) clearTimeout(this.collapseTimer);
      if (this.sticky) this.sticky.setAttribute('hidden', '');
    }

    /**
     * Move the Additional Warranty block into the wizard as its final question,
     * so it appears only after the flow is complete. It is moved rather than
     * copied: assets/product.js looks up a single `[data-warranty-picker]` when
     * adding the warranty cart line.
     */
    adoptWarrantyPicker() {
      if (!this.warrantyStep) return;

      const picker = this.productWrapper.querySelector('[data-warranty-picker]');
      if (!picker || this.warrantyStep.contains(picker)) return;

      // Into the open panel, so it hides when the step collapses to its row.
      const open = this.warrantyStep.querySelector('[data-wizard-warranty-open]');
      (open || this.warrantyStep).appendChild(picker);

      // It is hidden inline so it cannot flash up before this runs. Visibility
      // is the wizard step's job from here on.
      picker.style.display = '';

      // The picker is shared with products that have no wizard, so the number
      // badge and progress row are added here rather than in
      // snippets/warranty-picker.liquid.
      const labelRow = picker.querySelector('.form__label');
      if (labelRow && !labelRow.querySelector('.wizard-step__number')) {
        const badge = document.createElement('span');
        badge.className = 'wizard-step__number';
        badge.setAttribute('aria-hidden', 'true');
        labelRow.insertBefore(badge, labelRow.firstChild);
      }
      if (open && !open.querySelector('.wizard-step__banner')) {
        const banner = document.createElement('div');
        banner.className = 'wizard-step__banner';
        banner.setAttribute('aria-hidden', 'true');
        banner.textContent = 'Current step';
        open.insertBefore(banner, open.firstChild);
      }
      if (labelRow && !this.warrantyStep.querySelector('.wizard-step__progress')) {
        const progress = document.createElement('div');
        progress.className = 'wizard-step__progress';
        progress.innerHTML =
          '<span class="wizard-step__progress-track" role="progressbar" aria-label="Setup progress" data-wizard-progress-track>' +
          '<span class="wizard-step__progress-bar" data-wizard-progress-bar></span>' +
          '</span>';
        labelRow.insertAdjacentElement('afterend', progress);
      }
    }

    /**
     * Tick the only answer of any single-answer question, so it never costs a
     * tap. Done before the first render, so such a step is simply never shown.
     */
    autoSelectSingleChoices() {
      this.steps.forEach((step) => {
        const radios = this.radiosOf(step);
        if (radios.length === 1 && !radios[0].checked) radios[0].checked = true;
      });
    }

    /**
     * Give every rendered step a unique id, and rename its radios to match.
     *
     * Two choices can lead to the same question, and each renders its own copy
     * — both are needed, because they carry different prices. Only the ids and
     * radio group names have to be made unique, or answering one copy would
     * also answer the other.
     */
    deduplicateStepIds() {
      const seen = new Map();

      this.querySelectorAll('[data-wizard-step]').forEach((step) => {
        const key = step.dataset.wizardStep;
        const count = seen.get(key) || 0;
        seen.set(key, count + 1);
        if (count === 0) return;

        const suffix = `--${count}`;
        step.dataset.wizardStepInstance = key + suffix;

        this.radiosOf(step).forEach((input) => {
          input.name += suffix;
          const label = input.id && step.querySelector(`label[for="${input.id}"]`);
          const tip = input.id && step.querySelector(`#${escapeId(input.id)}-tip`);
          const info =
            input.id &&
            step.querySelector(`[aria-controls="${escapeId(input.id)}-tip"]`);
          input.id += suffix;
          if (label) label.setAttribute('for', input.id);
          if (tip) tip.id = `${input.id}-tip`;
          if (info) info.setAttribute('aria-controls', `${input.id}-tip`);
        });
      });
    }

    onChange = (event) => {
      if (event.target.matches('[data-wizard-step] input[type="radio"]')) {
        this.onAnswer(event.target);
        return;
      }
      // The warranty picker sits inside the wizard but is not a wizard step.
      if (event.target.matches('[data-warranty-picker] input')) {
        this.onAnswer(event.target);
      }
    };

    /**
     * An answer was given: show the tick briefly, then collapse this step and
     * move on. Changing an answer that is already collapsed skips the pause.
     */
    onAnswer(input) {
      const step = input.closest('[data-wizard-step]') || this.warrantyStep;
      const wasEditing = step === this.currentStep;

      // The browser unticks the previous radio before firing `change`, so the
      // answer being replaced is only knowable from what was recorded at the
      // last render. Undo needs it to put this step back too.
      const previous = this.lastAnswers ? this.lastAnswers.get(step) : null;

      // A different product means a different warranty price, so the old pick
      // cannot carry over. Answering the warranty itself is not a change.
      if (step !== this.warrantyStep) this.clearWarranty();

      // Clearing happens before the render so the note can describe it.
      this.clearOrphanedAnswers(step, previous);
      this.render();

      if (!wasEditing) return;
      if (this.collapseTimer) clearTimeout(this.collapseTimer);
      this.collapseTimer = setTimeout(() => {
        this.advanceFrom(step);
      }, COLLAPSE_DELAY);
    }

    /**
     * Keeping the same answer after Change fires no `change` event, so
     * advance here. A new answer still goes through onAnswer().
     */
    onRepick(input) {
      const step = input.closest('[data-wizard-step]') || this.warrantyStep;
      if (step !== this.currentStep) return;
      if (!this.lastAnswers || this.lastAnswers.get(step) !== input) return;

      if (this.collapseTimer) clearTimeout(this.collapseTimer);
      this.collapseTimer = setTimeout(() => {
        this.advanceFrom(step);
      }, COLLAPSE_DELAY);
    }

    /** Collapse the answered step and open the next unanswered one. */
    advanceFrom(step) {
      const path = this.fullPath();
      const index = path.indexOf(step);
      if (index === -1) return;

      const next = path.slice(index + 1).find((candidate) => !this.isAnswered(candidate));
      this.currentStep = next || null;
      this.render();

      const open = this.currentStep || next;

      if (open) {
        this.scrollToStep(open);
        this.focusStep(open);
      } else if (this.summary && !this.summary.hasAttribute('hidden')) {
        this.scrollToStep(this.summary);
      }
    }

    onClick = (event) => {
      if (event.target.matches('[data-wizard-step] input[type="radio"], [data-warranty-picker] input')) {
        this.onRepick(event.target);
        return;
      }

      // The whole collapsed row acts as its Change button.
      const change = event.target.closest('[data-wizard-change], [data-wizard-step-summary]');
      if (change) {
        this.reopen(change.closest('[data-wizard-step], [data-wizard-warranty]'));
        return;
      }

      const undo = event.target.closest('[data-wizard-undo]');
      if (undo) {
        this.applyUndo();
        return;
      }

      const close = event.target.closest('[data-wizard-tooltip-close]');
      if (close) {
        const card = close.closest('.wizard-choice');
        const info = card && card.querySelector('[data-wizard-tooltip]');
        if (info) this.setTooltip(info, false);
        return;
      }

      const info = event.target.closest('[data-wizard-tooltip]');
      if (info) {
        // Inside a <label> on some layouts, so the radio must not be ticked.
        event.preventDefault();
        event.stopPropagation();

        const open = info.getAttribute('aria-expanded') !== 'true';
        // A clicked popover stays put until it is dismissed, so moving the
        // pointer away does not take it with it. Set after setTooltip(),
        // which clears the pin as it closes whatever was open before.
        this.setTooltip(info, open);
        this.pinnedTooltip = open ? info : null;
      }
    };

    onKeydown = (event) => {
      // A collapsed row is tappable as a whole; keep it usable from a keyboard.
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const summary = event.target.closest('[data-wizard-step-summary]');
      if (!summary || event.target.closest('[data-wizard-change]')) return;
      event.preventDefault();
      this.reopen(summary.closest('[data-wizard-step], [data-wizard-warranty]'));
    };

    /**
     * Open on hover or focus, desktop only — a tap on a touch screen fires a
     * synthetic mouseover, which would open the popover and immediately have
     * it closed again by the click handler.
     *
     * The tooltip itself counts as part of the target, so moving the pointer
     * onto it (to read a long note, or to reach the close button) does not
     * dismiss it.
     */
    onPointerOver = (event) => {
      if (!this.canHover()) return;

      const info = this.tooltipTriggerFor(event.target);
      if (!info || info === this.hoveredTooltip) return;

      // After setTooltip(), which clears these as it closes what was open.
      this.setTooltip(info, true);
      this.hoveredTooltip = info;
    };

    onPointerOut = (event) => {
      if (!this.canHover() || !this.hoveredTooltip) return;

      // Ignore moves that stay inside the same button or its popover.
      const to = event.relatedTarget;
      if (to && this.tooltipTriggerFor(to) === this.hoveredTooltip) return;

      const info = this.hoveredTooltip;
      this.hoveredTooltip = null;

      // A click leaves it pinned open; only a hover-opened one closes here.
      if (this.pinnedTooltip === info) return;
      this.setTooltip(info, false);
    };

    /** The info button a hovered element belongs to, button or popover alike. */
    tooltipTriggerFor(node) {
      if (!node || typeof node.closest !== 'function') return null;

      const info = node.closest('[data-wizard-tooltip]');
      if (info) return info;

      const tip = node.closest('.wizard-choice__tooltip');
      if (!tip) return null;

      const card = tip.closest('.wizard-choice');
      return card ? card.querySelector('[data-wizard-tooltip]') : null;
    }

    onDocumentClick = (event) => {
      if (this.contains(event.target)) return;
      this.closeAllTooltips();
    };

    onDocumentKeydown = (event) => {
      if (event.key === 'Escape') this.closeAllTooltips();
    };

    setTooltip(info, open) {
      const card = info.closest('.wizard-choice');
      const tip = card && card.querySelector('.wizard-choice__tooltip');
      if (!tip) return;

      if (open) this.closeAllTooltips();
      info.setAttribute('aria-expanded', open ? 'true' : 'false');
      tip.toggleAttribute('hidden', !open);
      if (open) this.positionTooltip(info, tip);
    }

    /**
     * Place a desktop popover against its button.
     *
     * It is `position: fixed` (the card clips its contents, so an absolute
     * one would be cut off), which means viewport coordinates have to be
     * worked out here. On mobile the tooltip is an ordinary block inside the
     * card and needs none of this.
     */
    positionTooltip(info, tip) {
      if (typeof info.getBoundingClientRect !== 'function') return;

      const button = info.getBoundingClientRect();
      const box = tip.getBoundingClientRect();
      const margin = 8;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

      // Centred on the button, then pulled back inside the viewport.
      let left = button.left + button.width / 2 - box.width / 2;
      left = Math.max(margin, Math.min(left, viewportWidth - box.width - margin));

      // Always below the button, so the popover never covers the option it
      // describes and the arrow always points the same way.
      tip.style.left = `${Math.round(left)}px`;
      tip.style.top = `${Math.round(button.bottom + margin)}px`;

      // Keep the arrow pointing at the button, wherever the box ended up.
      const arrow = button.left + button.width / 2 - left;
      tip.style.setProperty('--wizard-tooltip-arrow', `${Math.round(arrow)}px`);
    }

    /**
     * Whether the device has a real pointer that can hover.
     *
     * Screen width is the wrong question: a touch screen fires a synthetic
     * mouseover on tap, which would open the popover only for the click
     * handler to close it again, leaving the button looking dead. A large
     * touch screen has the same problem as a small one.
     */
    canHover() {
      return typeof window.matchMedia === 'function'
        ? window.matchMedia('(hover: hover)').matches
        : false;
    }

    closeAllTooltips() {
      this.pinnedTooltip = null;
      this.hoveredTooltip = null;

      this.querySelectorAll('[data-wizard-tooltip]').forEach((info) => {
        info.setAttribute('aria-expanded', 'false');
      });
      this.querySelectorAll('.wizard-choice__tooltip').forEach((tip) => {
        tip.setAttribute('hidden', '');
      });
    }

    /** Reopen an answered step for editing, keeping its answer selected. */
    reopen(step) {
      if (!step) return;
      this.currentStep = step;
      this.render();
      this.scrollToStep(step);
      this.focusStep(step);
    }

    /**
     * Drop only the answers that no longer fit after `step` changed, and offer
     * Undo. Answers on questions still reachable — the warranty, typically —
     * are left alone, so changing a mask does not cost the customer their
     * warranty choice.
     */
    clearOrphanedAnswers(step, previous) {
      const reachable = new Set(this.fullPath());
      const cleared = [];

      this.steps.forEach((candidate) => {
        if (reachable.has(candidate)) return;
        const checked = this.checkedOf(candidate);
        if (!checked) return;
        cleared.push({ step: candidate, input: checked });
      });

      if (!cleared.length) {
        this.hideNote();
        return;
      }

      cleared.forEach(({ input }) => {
        input.checked = false;
      });

      // The step the customer now lands on is where the note belongs.
      const path = this.fullPath();
      const index = path.indexOf(step);
      const landing = path.slice(index + 1).find((s) => !this.isAnswered(s));
      const label = this.checkedOf(step);

      if (landing) this.showNote(landing, this.resetNoteFor(landing, label));

      // After showNote(), which clears any previous offer. Undo has to put
      // back the answer that caused the clearing as well, or the restored
      // steps would fall off the path again and be cleared a second time.
      this.pendingUndo = previous ? [{ step, input: previous }, ...cleared] : cleared;
    }

    /**
     * The landing step's own Reset note wins; `{choice}` becomes the new
     * answer. Without one, the built-in wording is used.
     */
    resetNoteFor(landing, label) {
      const choice = label && label.dataset.label;
      const custom = landing.dataset.resetNote;
      if (custom) return custom.replace(/\{choice\}/g, choice || '');
      return choice
        ? `We switched you to ${choice}. Please pick again.`
        : 'Your later answers no longer fit. Please pick again.';
    }

    showNote(step, text) {
      this.hideNote();

      const note = step.querySelector('[data-wizard-note]');
      if (!note) return;

      const textEl = note.querySelector('[data-wizard-note-text]');
      if (textEl) textEl.textContent = text;
      note.removeAttribute('hidden');
      // No timeout: stays until Undo or the next answer, so it is not missed.
      this.activeNote = note;
    }

    hideNote() {
      if (this.activeNote) this.activeNote.setAttribute('hidden', '');
      this.activeNote = null;
      this.pendingUndo = null;
    }

    /** Put back the answers the last change cleared. */
    applyUndo() {
      const cleared = this.pendingUndo;
      this.hideNote();
      if (!cleared || !cleared.length) return;

      // The answer that caused the clearing has to go back first, or the
      // restored steps are still off the path and would be cleared again.
      const branch = cleared[0].step;
      cleared.forEach(({ input }) => {
        input.checked = true;
      });

      this.currentStep = branch;
      this.render();
      this.scrollToStep(branch);
    }

    /**
     * The question that follows a chosen answer. Every question is a sibling
     * here, so it is found by key among this wizard's steps — two branches can
     * lead to the same question, and the first unclaimed copy is taken.
     */
    nextStepFor(checked) {
      const key = checked.dataset.nextStep;
      if (!key) return null;

      const step = checked.closest('[data-wizard-step]');

      // Prefer a copy after the step just answered, so converging branches
      // each resolve to their own instance rather than sharing the first.
      if (step) {
        let sibling = step.nextElementSibling;
        while (sibling) {
          if (sibling.matches('[data-wizard-step]') && sibling.dataset.wizardStep === key) {
            return sibling;
          }
          sibling = sibling.nextElementSibling;
        }
      }

      return this.steps.find((candidate) => candidate.dataset.wizardStep === key) || null;
    }

    /**
     * A step's own radios. Size buttons belong to the card of the choice that
     * heads them, but are still this step's answers.
     */
    radiosOf(step) {
      return Array.from(step.querySelectorAll('input[type="radio"]')).filter(
        (input) => input.closest('[data-wizard-step]') === step
      );
    }

    checkedOf(step) {
      if (step === this.warrantyStep) {
        return (
          step.querySelector('[data-warranty-picker] input[name="warranty_option"]:checked') ||
          null
        );
      }
      return this.radiosOf(step).find((input) => input.checked) || null;
    }

    isAnswered(step) {
      return Boolean(this.checkedOf(step));
    }

    /**
     * Whether everything needed to buy has been decided — the warranty
     * included, since nothing is ticked for the customer any more.
     */
    isComplete(path) {
      if (!this.selectedVariantId(path)) return false;
      return path.every((step) => this.isAnswered(step));
    }

    /** This step's own error element, not a nested question's. */
    errorOf(step) {
      return (
        Array.from(step.querySelectorAll('.form__option_error')).find(
          (el) => el.closest('[data-wizard-step]') === step
        ) || null
      );
    }

    /**
     * The steps reachable from the current answers, in order. Stops at the
     * first unanswered question, since nothing beyond it is decided yet.
     */
    currentPath() {
      const path = [];
      const visited = new Set();
      let step = this.steps[0];

      while (step && !visited.has(step)) {
        visited.add(step);
        path.push(step);
        const checked = this.checkedOf(step);
        if (!checked) break;
        step = this.nextStepFor(checked);
      }
      return path;
    }

    /**
     * The path including the warranty question, which is what the customer
     * actually walks through. The warranty only joins once a variant is
     * resolved, since there is nothing to insure before then.
     */
    fullPath() {
      const path = this.currentPath();
      if (this.warrantyStep && this.warrantyStep.firstElementChild && this.hasWarrantyOptions()) {
        if (this.selectedVariantId(path)) path.push(this.warrantyStep);
      }
      return path;
    }

    hasWarrantyOptions() {
      return Boolean(
        this.warrantyStep &&
          this.warrantyStep.querySelector('[data-warranty-picker] input[name="warranty_option"]')
      );
    }

    render() {
      // The first render is the page load: nothing to animate from.
      const before = this.hasRendered ? this.measureSteps() : null;
      this.hasRendered = true;

      const path = this.fullPath();
      const onPath = new Set(path);

      // Clear answers that fell off the path, so a changed branch cannot leave
      // a variant selected behind a hidden step.
      this.steps.forEach((step) => {
        if (onPath.has(step)) return;
        step.setAttribute('hidden', '');
        step.classList.remove('is-current', 'is-answered', 'is-answered-group');
        this.radiosOf(step).forEach((input) => {
          input.checked = false;
        });
        this.hideError(step);
      });

      // The current step is the one being edited, or the first unanswered one.
      if (!this.currentStep || !onPath.has(this.currentStep)) {
        this.currentStep = path.find((step) => !this.isAnswered(step)) || null;
      }

      // Steps after the current one stay hidden: the flow is still one
      // question at a time, however many are already answered.
      const currentIndex = this.currentStep ? path.indexOf(this.currentStep) : path.length;

      path.forEach((step, index) => {
        const isCurrent = step === this.currentStep;
        const beyond = currentIndex !== -1 && index > currentIndex;

        const answered = this.isAnswered(step);

        step.toggleAttribute('hidden', beyond);
        step.classList.toggle('is-current', isCurrent);
        step.classList.toggle('is-answered', !isCurrent && answered);

        // Size buttons are how a size-picker card gets answered, so a step
        // the customer can act on must show all of them — including one
        // reopened through Change, where the point is to pick something else.
        // Only a settled step narrows down to the chosen card's sizes.
        // See .is-answered-group in the stylesheet.
        step.classList.toggle('is-answered-group', answered && !isCurrent);

        this.renderStepSummary(step, isCurrent);
      });

      this.markChosenCards(path);
      this.numberSteps(path);
      this.recordAnswers(path);

      const variantId = this.selectedVariantId(path);
      this.applyVariant(variantId);
      this.renderSummary(path);
      this.renderSticky(path);
      this.revalidate(path);
      this.animateSteps(before);
    }

    animatedSteps() {
      return this.warrantyStep ? [...this.steps, this.warrantyStep] : this.steps;
    }

    /** Heights as rendered now; a running animation reports its current frame. */
    measureSteps() {
      const heights = new Map();
      this.animatedSteps().forEach((step) => {
        heights.set(step, step.hasAttribute('hidden') ? 0 : step.offsetHeight);
      });
      return heights;
    }

    /**
     * Slide each step from its old height to its new one.
     * - Steps that became hidden just go: keeping them on screen would
     *   leave `hidden` out of step with the state other code reads.
     * - Scrolling waits for the slide to finish (see scrollToStep).
     */
    animateSteps(before) {
      if (!before || typeof Element.prototype.animate !== 'function') return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.animatedSteps().forEach((step) => {
        const from = before.get(step);
        if (from === undefined || step.hasAttribute('hidden')) return;

        // Cancel first, so the height read below is the step's real one.
        if (step.wizardAnimation) step.wizardAnimation.cancel();
        const to = step.offsetHeight;
        if (from === to) return;

        // Same value at both ends, so these hold for the whole slide.
        const fixed = { boxSizing: 'border-box', overflow: 'hidden' };
        const animation = step.animate(
          [
            { ...fixed, height: `${from}px` },
            { ...fixed, height: `${to}px` },
          ],
          { duration: STEP_ANIMATION, easing: 'ease' }
        );
        step.wizardAnimation = animation;
        animation.onfinish = animation.oncancel = () => {
          if (step.wizardAnimation === animation) step.wizardAnimation = null;
        };
        this.animationEndsAt = performance.now() + STEP_ANIMATION;
      });
    }

    /**
     * Remember what each step answers to, so the next change can tell Undo
     * which answer it replaced — by then the browser has already unticked it.
     */
    recordAnswers(path) {
      this.lastAnswers = new Map();
      path.forEach((step) => {
        const checked = this.checkedOf(step);
        if (checked) this.lastAnswers.set(step, checked);
      });
    }

    /** Fill in a step's collapsed row, and show it in place of the question. */
    renderStepSummary(step, isCurrent) {
      const row = step.querySelector('[data-wizard-step-summary]');
      if (!row) return;

      const answered = this.isAnswered(step);
      const collapsed = answered && !isCurrent;
      row.toggleAttribute('hidden', !collapsed);
      if (!collapsed) return;

      const answer = row.querySelector('[data-wizard-step-answer]');
      const checked = this.checkedOf(step);
      if (answer && checked) {
        answer.textContent = this.answerLabel(checked);
      }
      // Tappable as a whole, so the row is a control in its own right.
      if (!row.hasAttribute('tabindex')) {
        row.setAttribute('tabindex', '0');
        row.setAttribute('role', 'button');
      }
    }

    /** What a chosen input reads as in a summary row. */
    answerLabel(checked) {
      if (checked.dataset.label) return checked.dataset.label;
      const label = checked.id && this.querySelector(`label[for="${escapeId(checked.id)}"]`);
      return label ? label.textContent.trim() : '';
    }

    /** Flag the chosen card on each answered step, for the selected styling. */
    markChosenCards(path) {
      this.querySelectorAll('.wizard-choice').forEach((card) => {
        card.classList.remove('is-chosen');
      });
      path.forEach((step) => {
        const checked = this.checkedOf(step);
        if (!checked) return;
        const card = checked.closest('.wizard-choice');
        if (card) card.classList.add('is-chosen');
      });
    }

    /**
     * Number the questions down the visible path and fill in "Step X of Y".
     * Sibling branches share a depth, so numbering has to follow what is
     * actually on screen.
     */
    numberSteps(path) {
      const total = this.estimateTotalSteps(path);

      path.forEach((step, index) => {
        const n = index + 1;

        const badge = step.querySelector('.wizard-step__number');
        if (badge) badge.textContent = n;

        const rowIndex = step.querySelector('[data-wizard-step-index]');
        if (rowIndex) rowIndex.textContent = `${n} · `;

        // No visible "Step X of Y"; screen readers still get it.
        const track = step.querySelector('[data-wizard-progress-track]');
        if (track) {
          track.setAttribute('aria-valuemin', '1');
          track.setAttribute('aria-valuemax', total);
          track.setAttribute('aria-valuenow', n);
          track.setAttribute('aria-valuetext', `Step ${n} of ${total}`);
        }

        const bar = step.querySelector('[data-wizard-progress-bar]');
        if (bar) bar.style.width = `${Math.round((n / total) * 100)}%`;
      });

      this.stepCount = total;
      this.stepIndex = this.currentStep ? path.indexOf(this.currentStep) + 1 : total;
    }

    /**
     * How many questions this setup will take in all.
     *
     * The answered path is known; beyond the open question it is a guess, so
     * take the longest branch still reachable from it.
     *
     * No estimate is right for every branch — this tree runs from two
     * questions to four depending on the answers — and of the ways to be
     * wrong, a total that shrinks under the customer is the worst. Counting
     * the longest branch means the figure can only ever fall, which reads as
     * "that was quicker than expected" rather than as the goal moving.
     */
    estimateTotalSteps(path) {
      const open = path[path.length - 1];
      if (!open || this.isAnswered(open)) return path.length;

      // The warranty joins `path` only once a variant is resolved, so it is
      // added here exactly when it is not already counted.
      const warranty =
        this.warrantyStep && this.hasWarrantyOptions() && !path.includes(this.warrantyStep)
          ? 1
          : 0;

      // The open question is already counted, so measure what follows it.
      return path.length - 1 + this.longestBranchFrom(open, new Set()) + warranty;
    }

    /** Questions from `step` down its deepest branch, `step` included. */
    longestBranchFrom(step, seen) {
      if (!step || seen.has(step)) return 0;
      seen.add(step);

      let deepest = 0;
      this.radiosOf(step).forEach((input) => {
        const next = this.nextStepFor(input);
        const depth = next ? this.longestBranchFrom(next, new Set(seen)) : 0;
        if (depth > deepest) deepest = depth;
      });

      return 1 + deepest;
    }

    /**
     * The variant named by the deepest answer on the path. Guiding questions
     * carry no variant, so the last one that does is the answer.
     */
    selectedVariantId(path) {
      let id = null;
      path.forEach((step) => {
        const checked = this.checkedOf(step);
        if (checked && checked.dataset.variantId) id = checked.dataset.variantId;
      });
      return id;
    }

    applyVariant(variantId) {
      if (variantId === this.appliedVariantId) return;
      this.appliedVariantId = variantId;

      this.setFormVariantId(variantId);
      this.refreshProductInfo(variantId);
    }

    /**
     * Write the id the product form submits. Mirrors VariantSelects'
     * updateVariantInput(), including the installment form and the `change`
     * event other scripts listen for.
     */
    setFormVariantId(variantId) {
      const section = this.dataset.section;
      const forms = document.querySelectorAll(
        `#product-form-${section}, #product-form-installment`
      );

      forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variantId || '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Refresh price, SKU, inventory and the Add to cart state by handing the
     * variant to VariantSelects and reusing its section render. Doing it there
     * keeps one implementation of that fetch.
     */
    refreshProductInfo(variantId) {
      const vs = this.variantSelects;
      if (!vs || typeof vs.renderProductInfo !== 'function') return;

      if (!variantId) {
        if (typeof vs.toggleAddButton === 'function') {
          vs.toggleAddButton(true, '', true);
        }
        // Clearing an answer must clear the image it chose, or the page shows
        // a mask the customer is no longer buying.
        this.resetMedia();
        return;
      }

      const variant = this.variantById(variantId);
      if (!variant) return;

      vs.currentVariant = variant;
      vs.renderProductInfo();
      if (typeof vs.updateURL === 'function') vs.updateURL();
      if (typeof vs.updateMedia === 'function') vs.updateMedia();

      this.watchForRerenders();
    }

    /** Put the gallery back to the product's own first image. */
    resetMedia() {
      const vs = this.variantSelects;
      if (!vs || typeof vs.updateMedia !== 'function') return;

      const data = typeof vs.getVariantData === 'function' ? vs.getVariantData() : null;
      const first = Array.isArray(data)
        ? data.find((variant) => variant.featured_media)
        : null;
      if (!first) return;

      // updateMedia() reads currentVariant, so the default has to be set on it.
      vs.currentVariant = first;
      vs.updateMedia();
      vs.currentVariant = null;
    }

    /**
     * renderProductInfo() swaps whole sections in when its fetch returns,
     * which can drop the total from a re-rendered button. Watching for that
     * beats guessing a delay, since the fetch has no completion event.
     */
    watchForRerenders() {
      if (this.rerenderObserver || typeof MutationObserver === 'undefined') return;

      this.rerenderObserver = new MutationObserver(() => {
        const path = this.fullPath();

        // toggleAddButton() rewrites the label's textContent, taking the total
        // span with it.
        const lostTotal = Array.from(
          document.querySelectorAll('.single-add-to-cart-button--text')
        ).some((label) => !label.querySelector('[data-wizard-total]'));

        // …and re-enables a button the flow may not be ready for.
        const ready = this.isComplete(path);
        const wronglyEnabled = Array.from(
          document.querySelectorAll('.single-add-to-cart-button')
        ).some((button) => !ready && !button.hasAttribute('disabled'));

        if (!lostTotal && !wronglyEnabled) return;

        if (lostTotal) this.renderSummary(path);
        this.revalidate(path);

        // revalidate() writes `disabled`, which this observer is watching.
        // Callbacks are microtasks, so a plain flag would already have reset
        // by the time the next one runs: drop the records it just queued
        // instead, or the two would trigger each other forever.
        this.rerenderObserver.takeRecords();
      });

      this.rerenderObserver.observe(this.productWrapper, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }

    variantById(variantId) {
      const vs = this.variantSelects;
      if (!vs || typeof vs.getVariantData !== 'function') return null;

      const data = vs.getVariantData();
      if (!Array.isArray(data)) return null;
      return data.find((variant) => String(variant.id) === String(variantId)) || null;
    }

    /** Recap of the answers, plus the warranty line, once a variant is picked. */
    renderSummary(path) {
      if (!this.summary || !this.summaryBody) return;

      const variantId = this.selectedVariantId(path);
      const complete = this.isComplete(path);

      if (!complete) {
        this.summary.setAttribute('hidden', '');
        this.updateAddButtonTotal(variantId ? this.totalFor(path) : null);
        this.renderRemaining(path);
        return;
      }

      this.summaryBody.innerHTML = '';
      this.summaryLines(path).forEach((line) => {
        const row = document.createElement('li');
        row.className = 'wizard-summary__line';

        // Always present, even without a picture, so the labels line up and
        // the rows keep an even height. Decorative: the label beside it
        // already names the item.
        const thumb = document.createElement('span');
        thumb.className = 'wizard-summary__line-thumb';
        if (line.thumbnail) {
          const image = document.createElement('img');
          image.src = line.thumbnail;
          image.alt = '';
          // setAttribute, not the property: older browsers do not reflect it.
          image.setAttribute('loading', 'lazy');
          thumb.appendChild(image);
        }

        const label = document.createElement('span');
        label.className = 'wizard-summary__line-label';
        label.textContent = line.label;

        // The product's own row is the thing being added to, not an addition.
        const amount = document.createElement('span');
        amount.className = 'wizard-summary__line-price';
        amount.textContent = line.base
          ? this.formatMoney(line.price)
          : `+${this.formatMoney(line.price)}`;

        row.append(thumb, label, amount);
        this.summaryBody.appendChild(row);
      });

      this.summary.removeAttribute('hidden');

      const total = this.totalFor(path);
      if (this.summaryTotal) this.summaryTotal.textContent = this.formatMoney(total);
      this.updateAddButtonTotal(total);
      this.renderRemaining(path);
    }

    /**
     * One row per answer that costs something, for the "Your setup" panel.
     *
     * Every row reads as "+$…", the same way the cards do, so a figure means
     * the same thing wherever it appears. An answer that adds nothing — a
     * guiding question, or a choice priced at the baseline — is left out
     * rather than shown with a blank or a zero.
     *
     * A choice's data-price is the whole product at that variant, not what the
     * answer adds, so each one is measured against what was already committed:
     * the baseline from Liquid to begin with, then the last priced answer.
     */
    summaryLines(path) {
      const base = parseInt(this.dataset.basePrice, 10) || 0;
      const lines = [];
      let running = base;

      // The product itself, so the "+$…" rows have something to add onto and
      // the rows still account for the total. Without it a customer who picks
      // the cheapest setup sees a total with nothing above it.
      if (base > 0) {
        lines.push({
          label: this.productLabel(),
          price: base,
          base: true,
          thumbnail: this.dataset.baseThumbnail || '',
        });
      }

      path.forEach((step) => {
        const checked = this.checkedOf(step);
        if (!checked) return;

        const label = this.answerLabel(checked);
        if (!label) return;

        if (step === this.warrantyStep) {
          // "No Thanks" costs nothing, so it is not a line.
          const warranty = this.chosenWarranty();
          if (warranty) {
            lines.push({
              // Its own label, not answerLabel(): the picker spells the price
              // out, which the price column already shows.
              label: warranty.label,
              price: warranty.price,
              thumbnail: warranty.thumbnail,
            });
          }
          return;
        }

        if (!checked.dataset.price) return;

        const price = parseInt(checked.dataset.price, 10);
        const added = price - running;
        running = price;

        if (added > 0) {
          lines.push({
            // Summary title is optional; the answer's own label otherwise.
            label: checked.dataset.summaryTitle || label,
            price: added,
            thumbnail: checked.dataset.thumbnail || '',
          });
        }
      });

      return lines;
    }

    /** The product's own name, for the baseline row of the summary. */
    productLabel() {
      const heading =
        this.productWrapper.querySelector &&
        this.productWrapper.querySelector('.product-title, .product__title, h1');
      const text = heading ? heading.textContent.trim() : '';
      return text || 'Base product';
    }

    /** "1 step left before your setup is complete", or nothing when done. */
    renderRemaining(path) {
      if (!this.remaining) return;

      const answeredCount = path.filter((step) => this.isAnswered(step)).length;

      // `path` stops at the first unanswered question, so counting it would
      // always say "1 step left". The estimate already looks down the branch
      // ahead, which is the same figure the progress bar counts against.
      // A step is still open, so there is still something to do.
      const left = Math.max(
        this.estimateTotalSteps(path) - answeredCount,
        this.currentStep ? 1 : 0
      );
      if (left <= 0) {
        this.remaining.setAttribute('hidden', '');
        return;
      }

      // Before the customer is under way, say what is needed rather than how
      // much is left — "4 steps left" reads as a chore on first sight.
      this.remaining.textContent = !answeredCount
        ? 'Complete the steps above to add to cart'
        : left === 1
          ? '1 step left before your setup is complete'
          : `${left} steps left before your setup is complete`;
      this.remaining.removeAttribute('hidden');
    }

    /** The product's discounted price plus the warranty, in cents. */
    totalFor(path) {
      const variantId = this.selectedVariantId(path);
      if (!variantId) return 0;

      // data-price carries the discounted figure from Liquid; the variant JSON
      // holds the undiscounted one, so prefer the attribute.
      const chosen = this.chosenVariantInput(path);
      const variant = this.variantById(variantId);
      const base =
        chosen && chosen.dataset.price
          ? parseInt(chosen.dataset.price, 10)
          : variant
            ? variant.price
            : 0;

      const warranty = this.chosenWarranty();
      return base + (warranty ? warranty.price : 0);
    }

    /** The deepest answered choice that names a variant. */
    chosenVariantInput(path) {
      let input = null;
      path.forEach((step) => {
        const checked = this.checkedOf(step);
        if (checked && checked.dataset.variantId) input = checked;
      });
      return input;
    }

    /**
     * Untick the warranty, putting the step back to undecided.
     *
     * No `change` is dispatched: nothing outside the wizard listens — the cart
     * reads `:checked` when adding — and the wizard's own handler would take
     * the untick for an answer and collapse the step.
     */
    clearWarranty() {
      if (!this.warrantyStep) return;

      this.warrantyStep
        .querySelectorAll('[data-warranty-picker] input[name="warranty_option"]:checked')
        .forEach((radio) => {
          radio.checked = false;
        });
    }

    /** The picked warranty, or null for "No Thanks". */
    chosenWarranty() {
      if (!this.warrantyStep || this.warrantyStep.hasAttribute('hidden')) return null;

      const checked = this.warrantyStep.querySelector(
        '[data-warranty-picker] input[name="warranty_option"]:checked'
      );
      if (!checked || parseInt(checked.value, 10) <= 0) return null;

      // The picker reads "3 Years - $59.00": keep the coverage term, drop the
      // price the summary row states in its own column. Unnamed coverage
      // leaves the row as plain "Extended Warranty".
      const label = this.warrantyStep.querySelector(`label[for="${escapeId(checked.id)}"]`);
      const text = label ? label.textContent.trim() : '';
      let term = text.replace(/\s*[-–—]\s*[^-–—]*\d[\d.,]*\s*$/, '').trim();

      // A label that was only ever a price leaves no term to name.
      if (/^[^\w]*[\d.,]+[^\w]*$/.test(term)) term = '';

      return {
        label: term ? `Extended Warranty (${term})` : 'Extended Warranty',
        price: parseInt(checked.dataset.warrantyPrice, 10) || 0,
        thumbnail: checked.dataset.warrantyThumbnail || '',
      };
    }

    /**
     * Append the running total to every Add to cart button. The slot is
     * created when missing, so a button whose Liquid has no slot — the sticky
     * bar, or one re-rendered by VariantSelects — still shows the total.
     */
    updateAddButtonTotal(total) {
      const labels = document.querySelectorAll('.single-add-to-cart-button--text');

      labels.forEach((label) => {
        let slot = label.querySelector('[data-wizard-total]');
        if (!slot) {
          if (total === null) return;
          slot = document.createElement('span');
          slot.setAttribute('data-wizard-total', '');
          label.appendChild(slot);
        }
        slot.textContent = total === null ? '' : ` · ${this.formatMoney(total)}`;
      });
    }

    /**
     * The mobile bar: progress and total while the flow runs, Add to cart once
     * it is done. Its own visibility is handled by watchStickyVisibility().
     */
    renderSticky(path) {
      if (!this.sticky) return;

      const complete = this.isComplete(path);
      const progress = this.sticky.querySelector('[data-wizard-sticky-progress]');
      const total = this.sticky.querySelector('[data-wizard-sticky-total]');
      const button = this.sticky.querySelector('[data-wizard-sticky-button]');

      if (progress) {
        progress.textContent = complete
          ? 'Setup complete'
          : `Step ${this.stepIndex} of ${this.stepCount}`;
      }
      if (total) {
        const amount = this.totalFor(path);
        total.textContent = amount ? this.formatMoney(amount) : '';
      }
      if (button) {
        button.textContent = complete ? 'Add to cart' : 'Next step ↑';
        button.classList.toggle('is-complete', complete);
        button.onclick = complete
          ? () => this.submitFromSticky()
          : () => {
              if (this.currentStep) {
                this.scrollToStep(this.currentStep);
                this.focusStep(this.currentStep);
              }
            };
      }
    }

    submitFromSticky() {
      const button = document.querySelector(
        `#product-form-${this.dataset.section} [name="add"], .single-add-to-cart-button`
      );
      if (button) button.click();
    }

    /**
     * Show the bar once the flow has scrolled past, and hide it again when the
     * real Add to cart is on screen — two buttons doing the same job at once
     * would be confusing.
     */
    watchStickyVisibility() {
      if (!this.sticky || typeof IntersectionObserver === 'undefined') return;

      const addToCart = document.querySelector(
        `#product-form-${this.dataset.section} [name="add"]`
      );
      const firstStep = this.steps[0];
      const state = new Map();

      this.stickyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => state.set(entry.target, entry.isIntersecting));

        const startVisible = firstStep ? state.get(firstStep) !== false : true;
        const buttonVisible = addToCart ? state.get(addToCart) === true : false;
        this.sticky.toggleAttribute('hidden', startVisible || buttonVisible);
      });

      if (firstStep) this.stickyObserver.observe(firstStep);
      if (addToCart) this.stickyObserver.observe(addToCart);
    }

    scrollToStep(element) {
      if (!element || typeof element.getBoundingClientRect !== 'function') return;
      if (typeof window.scrollTo !== 'function') return;

      // Measure after the collapse that preceded this has been laid out:
      // a step closing shortens the page, and the browser clamps the scroll
      // position to the new height. Measuring first would aim at the old
      // layout and the clamp would read as a jump to the top.
      const run = () => {
        const rect = element.getBoundingClientRect();

        // Already comfortably in view — moving the page under the customer
        // after every answer is more disruptive than leaving it still. A block
        // taller than the viewport only needs its top on screen.
        const fits = rect.bottom <= window.innerHeight || rect.height > window.innerHeight - SCROLL_MARGIN;
        if (rect.top >= SCROLL_MARGIN && fits) return;

        try {
          window.scrollTo({
            top: rect.top + window.scrollY - SCROLL_MARGIN,
            behavior: 'smooth',
          });
        } catch (error) {
          // Scrolling is a nicety; never let it break the flow.
        }
      };

      // Mid-slide positions are not final; aim once the steps have settled.
      const wait = (this.animationEndsAt || 0) - performance.now();
      if (wait > 0) {
        setTimeout(run, wait);
      } else if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(run);
      } else {
        run();
      }
    }

    /** Move focus to a newly opened question, so it is announced. */
    focusStep(step) {
      const heading = step.querySelector('.wizard-step__question, .form__label');
      if (!heading) return;
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      if (typeof heading.focus === 'function') heading.focus({ preventScroll: true });
    }

    /** Matches the theme's money format, falling back to a plain amount. */
    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const format =
        window.theme &&
        window.theme.settings &&
        window.theme.settings.money_with_currency_format;

      if (!format) return `$${amount}`;
      return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }

    showError(step) {
      const error = this.errorOf(step);
      if (error) error.removeAttribute('hidden');
    }

    hideError(step) {
      const error = this.errorOf(step);
      if (error) error.setAttribute('hidden', '');
    }

    /**
     * Drop the error on any step that has since been answered, so a message
     * cannot outlive the problem it describes, and settle Add to cart on the
     * one rule validate() uses: enabled only when the flow names a variant and
     * no question is still open.
     *
     * The theme also writes this attribute (VariantSelects.toggleAddButton),
     * so it is reasserted on every render rather than set once — and it is
     * only ever tightened. A variant that is genuinely sold out stays disabled
     * whatever the wizard thinks.
     */
    revalidate(path) {
      path.forEach((step) => {
        if (this.isAnswered(step)) this.hideError(step);
      });

      const variantId = this.selectedVariantId(path);
      const ready = this.isComplete(path);
      const variant = variantId ? this.variantById(variantId) : null;
      const soldOut = Boolean(variant) && variant.available === false;

      document.querySelectorAll('.single-add-to-cart-button').forEach((button) => {
        if (!ready || soldOut) {
          button.setAttribute('disabled', 'disabled');
        } else {
          button.removeAttribute('disabled');
        }
        button.classList.toggle('wizard-incomplete', !ready);
      });
    }

    /**
     * True when every required question on the current path is answered and a
     * variant has been resolved. Called by the add-to-cart handler in
     * assets/product.js.
     */
    validate() {
      const path = this.fullPath();
      let valid = true;
      let firstUnanswered = null;

      path.forEach((step) => {
        const answered = this.isAnswered(step);
        const required = step.dataset.required === 'true';
        if (required && !answered) {
          this.showError(step);
          if (!firstUnanswered) firstUnanswered = step;
          valid = false;
        } else {
          this.hideError(step);
        }
      });

      // A finished path must name a variant, or there is nothing to add.
      if (valid && !this.selectedVariantId(path)) {
        const last = path[path.length - 1];
        if (last) {
          this.showError(last);
          firstUnanswered = firstUnanswered || last;
        }
        valid = false;
      }

      if (firstUnanswered) {
        // Open it, or the error would be on a collapsed row.
        this.currentStep = firstUnanswered;
        this.render();
        this.scrollToStep(firstUnanswered);
        this.focusStep(firstUnanswered);
      }
      return valid;
    }
  }

  customElements.define('product-wizard', ProductWizard);
}
